import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Typography, Spin, Result, Flex } from 'antd';
import { LoginOutlined, LogoutOutlined, UserAddOutlined } from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { useApi } from '../../hooks/use_api';
import { useNodeInfo } from '../../hooks/use_node_info';
import NodeLogo from '../Node/NodeLogo';
import { EntityImage } from './EntityImage';
import PieceBackdrop from '../Skargard/PieceBackdrop';
import Fold from '../Skargard/Fold';
// Shares the Gathering guest join page's layout (.gathering-join,
// .gathering-join-card) so both public front doors look like one product.
import '../Gathering/Gathering.css';

const { Title, Paragraph, Text } = Typography;

function ClaimPage() {
    const api = useApi();
    const { token } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated, isLoading, keycloak, user, refreshProfile, logout } = useAuth();
    const { nodeName } = useNodeInfo();
    const [entity, setEntity] = useState(null);
    const [fetching, setFetching] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [accepting, setAccepting] = useState(false);
    const [acceptError, setAcceptError] = useState(null);
    // The signed-in account's own person, when it already has one: the claim
    // can't go through (accept_claim allows one person per account), so the
    // page says so and offers to sign out instead of trying and failing.
    const [signedInAs, setSignedInAs] = useState(null);

    useEffect(() => {
        api.get(`/api/entities/by-claim/${token}`)
            .then(data => { if (data) setEntity(data); })
            .catch(() => setNotFound(true))
            .finally(() => setFetching(false));
    }, [token]);

    // Once logged in, bind this Keycloak account to the claimed person entity —
    // unless it already has one. That's usually someone opening an invite for
    // somebody else while signed in as themselves (an admin testing the link,
    // a shared computer), and claiming would only fail. The lookup is done
    // here rather than read from AuthContext's userPersonEntity, which on this
    // page is still empty either way: not loaded yet, or deliberately never
    // created (see AuthContext).
    useEffect(() => {
        if (isLoading || !isAuthenticated || !entity || accepting || acceptError || signedInAs) return;
        setAccepting(true);
        api.get('/api/entities', { params: { entity_type: 'person', user_id: user.id } })
            // A failed lookup falls through to accept, which enforces the same
            // rule server-side.
            .catch(() => [])
            .then(own => {
                if (own?.length) {
                    setSignedInAs(own[0]);
                    setAccepting(false);
                    return undefined;
                }
                return api.post(`/api/entities/by-claim/${token}/accept`, undefined, { params: { user_id: user.id } })
            // accept_claim approved this registration and bound the person
            // entity server-side. Pick both up before handing over to a routed
            // page: /entity/:id sits behind ProtectedRoute, which reads the
            // status straight out of context, and the copy it renders for a
            // 'pending' user asks them to wait for an admin to approve them —
            // which is precisely what just happened.
                    .then(result => refreshProfile().then(() => result))
                    .then(result => navigate(`/entity/${result.id}`, { replace: true }));
            })
            .catch(err => setAcceptError(err.message || 'Could not claim this profile'));
    }, [isLoading, isAuthenticated, entity, token, user, accepting, acceptError, signedInAs, navigate, refreshProfile]);

    const redirectUri = window.location.href;

    // One wrapper for all four states: the piece keeps running across the
    // fetch -> sign-in -> accept transitions instead of being torn down and
    // reseeded on each one, which would restart the trails mid-claim.
    // Fold + followTheme match GuestJoinPage: the same backdrop in the
    // visitor's own light/dark mode.
    return (
        <PieceBackdrop piece={Fold} followTheme>
            <div className="gathering-join">
                {(fetching || isLoading || accepting) ? (
                    <Spin size="large" />
                ) : notFound ? (
                    /* Not status="404": antd reserves 404/403/500 for its stock
                       illustrations, and that branch is checked before the icon
                       prop, so the node logo could not replace the drawing. */
                    <Result
                        status="warning"
                        icon={<NodeLogo size={56} />}
                        title="Claim link not found"
                        subTitle="This link may have expired or already been used."
                    />
                ) : acceptError ? (
                    <Result
                        status="error"
                        icon={<NodeLogo size={56} />}
                        title="Could not claim this profile"
                        subTitle={acceptError}
                    />
                ) : (
                    <Card className="gathering-join-card">
                        <Flex vertical gap={16}>
                            <Flex vertical gap={4} align="center">
                                <NodeLogo size={56} />
                                <Title level={3} style={{ margin: '8px 0 0', textAlign: 'center' }}>{entity.name}</Title>
                                {/* invited_by: the admin who sent this link (by-claim
                                    lookup) — null for links minted before it was
                                    recorded, which keep the plain line. */}
                                {entity.invited_by ? (
                                    <Flex gap={8} align="center" justify="center" wrap>
                                        <EntityImage entity={entity.invited_by} size={24} />
                                        <Text strong>
                                            {entity.invited_by.name} invited you{nodeName ? ` to ${nodeName}` : ''}
                                        </Text>
                                    </Flex>
                                ) : (
                                    <Text strong>{nodeName ? `Claim your profile on ${nodeName}` : 'Claim your profile'}</Text>
                                )}
                                <Paragraph type="secondary" style={{ margin: 0, textAlign: 'center' }}>
                                    {signedInAs ? (
                                        <>
                                            You&apos;re signed in as <Text strong>{signedInAs.name}</Text>, which
                                            already has a profile here. Sign out so {entity.name} can sign in or
                                            create an account.
                                        </>
                                    ) : 'Sign in or create an account on this node.'}
                                </Paragraph>
                            </Flex>

                            {signedInAs ? (
                                <Flex vertical gap={10}>
                                    {/* Straight back to this link once signed out, so
                                        the right person lands on the sign-in card. */}
                                    <Button
                                        type="primary"
                                        icon={<LogoutOutlined />}
                                        block
                                        onClick={() => logout(window.location.href)}
                                    >
                                        Sign out and continue
                                    </Button>
                                    <Button block onClick={() => navigate(`/entity/${signedInAs.id}`)}>
                                        Go to my profile
                                    </Button>
                                </Flex>
                            ) : (
                                <Flex vertical gap={10}>
                                    <Button
                                        type="primary"
                                        icon={<UserAddOutlined />}
                                        block
                                        onClick={() => keycloak.register({ redirectUri })}
                                    >
                                        Create an account
                                    </Button>
                                    <Button
                                        icon={<LoginOutlined />}
                                        block
                                        onClick={() => keycloak.login({ redirectUri })}
                                    >
                                        Log in
                                    </Button>
                                </Flex>
                            )}
                        </Flex>
                    </Card>
                )}
            </div>
        </PieceBackdrop>
    );
}

export default ClaimPage;
